import { ChangeEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { TextField } from 'ui/shared/controls';
import { floorOverrideNormalizer } from './LegacyFloorOverrideNormalizer';
import { useCallback } from 'react';
import { FloorOverride } from 'shared/models';
import './LegacyFloorOverrideTable.css';

export type Props = {
    className?: string;
    values: FloorOverride[];
    onAddRow(): void;
    onRemoveRow(index: number): void;
    onChange(index: number, fnUpdate: (mapping: Readonly<FloorOverride>) => FloorOverride): void;
};

export const LegacyFloorOverrideTable = ({ className, values, onAddRow, onRemoveRow, onChange }: Props) => {
    const { t } = useTranslation();

    const handleClickRemove = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        const { index } = e.currentTarget.dataset;
        const i = parseInt(index!, 10);
        onRemoveRow(i);
    }, [ onRemoveRow ]);

    const handleChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const { dataset, value } = e.currentTarget;
        const { index, field } = dataset;
        const i = parseInt(index!, 10);
        onChange(i, mapping => ({
            ...mapping,
            [field!]: floorOverrideNormalizer(value),
        }));
    }, [ onChange ]);

    return (
        <div
            id="floor-override-table"
            className={ className }
        >
            <div>
                <label
                    className="_title"
                    htmlFor="floor-overrides"
                >
                    { t('DEVICE_CONFIGS.DIALOG.FIELD.FLOOR_OVERRIDES') }
                </label>
                <button
                    className="ui tiny icon button _add-button"
                    type="button"
                    onClick={ onAddRow }
                >
                    { t('COMMAND.ADD') }
                </button>
            </div>
            <table id="floor-overrides">
                <colgroup>
                    <col width="50%"/>
                    <col width="2em"/>
                    <col width="50%"/>
                    <col width="2em"/>
                </colgroup>
                <tbody>
                    { values.map((mapping, i) =>
                        <tr key={ `${i}` }>
                            <td>
                                <TextField
                                    id={ `floor-override-${i}-from` }
                                    maxLength={ 4 }
                                    data-index={ i }
                                    data-field="from"
                                    autoComplete="off"
                                    placeholder={ t('DEVICE_CONFIGS.DIALOG.FIELD.LOW_FLOOR') }
                                    value={ mapping.from }
                                    onChange={ handleChange }
                                />
                            </td>
                            <td className="icon">
                                <i className="grey angle right icon"/>
                            </td>
                            <td>
                                <TextField
                                    id={ `floor-override-${i}-to` }
                                    maxLength={ 4 }
                                    data-index={ i }
                                    data-field="to"
                                    autoComplete="off"
                                    placeholder={ t('DEVICE_CONFIGS.DIALOG.FIELD.HIGH_FLOOR') }
                                    value={ mapping.to }
                                    onChange={ handleChange }
                                />
                            </td>
                            <td className="icon">
                                <i
                                    className="red remove icon"
                                    title={ t('COMMAND.REMOVE') }
                                    data-index={ i }
                                    onClick={ handleClickRemove }
                                />
                            </td>
                        </tr>
                    ) }
                </tbody>
            </table>
        </div>
    );
};
